import React, {useState, useEffect, useCallback, useRef} from "react"
import {useCommandHistoryStore, FetchHistoryParams} from "../../store/commandHistoryStore"
import {useAuthStore, useIsReadOnly} from "../../store/authStore"

type SortField = "createdAt" | "updated" | "action" | "user_email"
type SortOrder = "asc" | "desc"

interface CommandHistoryPanelProps {
	isOpen: boolean
	onClose: () => void
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

const SortIcon: React.FC<{field: SortField; current: SortField; order: SortOrder}> = ({
	field,
	current,
	order,
}) => {
	if (field !== current) {
		return (
			<svg
				className="w-3 h-3 opacity-30 inline ml-1"
				fill="none"
				stroke="currentColor"
				viewBox="0 0 24 24">
				<path
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeWidth={2}
					d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"
				/>
			</svg>
		)
	}
	return order === "asc" ? (
		<svg
			className="w-3 h-3 inline ml-1 text-blue-500"
			fill="none"
			stroke="currentColor"
			viewBox="0 0 24 24">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
		</svg>
	) : (
		<svg
			className="w-3 h-3 inline ml-1 text-blue-500"
			fill="none"
			stroke="currentColor"
			viewBox="0 0 24 24">
			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
		</svg>
	)
}

const CommandHistoryPanel: React.FC<CommandHistoryPanelProps> = ({isOpen, onClose}) => {
	const {entries, pagination, isLoading, fetchHistory, updateDescription} = useCommandHistoryStore()

	const [onlyMe, setOnlyMe] = useState(false)
	const [editingId, setEditingId] = useState<string | null>(null)
	const [editingValue, setEditingValue] = useState("")
	const [savingId, setSavingId] = useState<string | null>(null)

	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(20)
	const [sortField, setSortField] = useState<SortField>("createdAt")
	const [sortOrder, setSortOrder] = useState<SortOrder>("desc")
	const [actionFilter, setActionFilter] = useState("")
	const [actionFilterInput, setActionFilterInput] = useState("")

	const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const panelRef = useRef<HTMLDivElement>(null)

	const {userProfile} = useAuthStore((state) => state)
	// Read-only roles may read history but not edit descriptions (backend answers 403 as well).
	const isReadOnly = useIsReadOnly()

	const buildParams = useCallback(
		(overrides?: Partial<FetchHistoryParams>): FetchHistoryParams => ({
			page,
			pageSize,
			sortField,
			sortOrder,
			actionFilter,
			...overrides,
		}),
		[page, pageSize, sortField, sortOrder, actionFilter],
	)

	useEffect(() => {
		if (isOpen) fetchHistory(buildParams())
	}, [isOpen])

	useEffect(() => {
		if (!isOpen) return
		fetchHistory(buildParams())
	}, [page, pageSize, sortField, sortOrder, actionFilter])

	useEffect(() => {
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === "Escape" && isOpen) onClose()
		}
		window.addEventListener("keydown", handleKey)
		return () => window.removeEventListener("keydown", handleKey)
	}, [isOpen, onClose])

	const handleSearchInput = (value: string) => {
		setActionFilterInput(value)
		if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
		searchDebounceRef.current = setTimeout(() => {
			setActionFilter(value)
			setPage(1)
		}, 400)
	}

	const handleSort = (field: SortField) => {
		if (field === sortField) {
			setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
		} else {
			setSortField(field)
			setSortOrder("desc")
		}
		setPage(1)
	}

	const filtered = onlyMe
		? entries.filter(
				(e) =>
					(userProfile?.email && e.user_email === userProfile.email) ||
					// History rows store the Cognito sub, which is userProfile.user_ID (not users.id).
					(userProfile?.user_ID && e.user_ID === userProfile.user_ID),
			)
		: entries

	const startEdit = (entry: (typeof entries)[number]) => {
		if (isReadOnly) return
		setEditingId(entry.id)
		setEditingValue(entry.description)
	}

	const cancelEdit = () => {
		setEditingId(null)
		setEditingValue("")
	}

	const saveEdit = async (id: string) => {
		setSavingId(id)
		try {
			await updateDescription(id, editingValue)
			setEditingId(null)
		} finally {
			setSavingId(null)
		}
	}

	const formatDate = (iso: string) => {
		try {
			return new Date(iso).toLocaleString()
		} catch {
			return iso
		}
	}

	const totalPages = pagination?.totalPages ?? 1
	const total = pagination?.total ?? 0

	const SortableTh: React.FC<{field: SortField; label: string}> = ({field, label}) => (
		<th
			className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap cursor-pointer select-none hover:text-gray-900 dark:hover:text-white transition-colors"
			onClick={() => handleSort(field)}>
			{label}
			<SortIcon field={field} current={sortField} order={sortOrder} />
		</th>
	)

	return (
		<>
			{isOpen && (
				<div
					className="fixed inset-0 bg-black/30 dark:bg-black/50 z-40 transition-opacity"
					onClick={onClose}
					aria-hidden="true"
				/>
			)}

			<div
				ref={panelRef}
				className={`fixed top-0 right-0 h-full w-full max-w-5xl z-50 flex flex-col bg-white dark:bg-gray-900 shadow-2xl transition-transform duration-300 ease-in-out ${
					isOpen ? "translate-x-0" : "translate-x-full"
				}`}
				role="dialog"
				aria-label="Command History"
				aria-modal="true">
				<div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
					<div className="flex items-center gap-3">
						<svg
							className="w-5 h-5 text-blue-600 dark:text-blue-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
							/>
						</svg>
						<h2 className="text-lg font-semibold text-gray-900 dark:text-white">Command History</h2>
						{!isLoading && (
							<span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full">
								{total} entries
							</span>
						)}
					</div>

					<div className="flex items-center gap-4">
						<label className="flex items-center gap-2 cursor-pointer select-none">
							<div className="relative">
								<input
									type="checkbox"
									className="sr-only"
									checked={onlyMe}
									onChange={(e) => setOnlyMe(e.target.checked)}
								/>
								<div
									className={`w-9 h-5 rounded-full transition-colors ${onlyMe ? "bg-blue-600" : "bg-gray-300 dark:bg-gray-600"}`}
								/>
								<div
									className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${onlyMe ? "translate-x-4" : "translate-x-0"}`}
								/>
							</div>
							<span className="text-sm text-gray-700 dark:text-gray-300">Only me</span>
						</label>

						<button
							onClick={() => fetchHistory(buildParams())}
							disabled={isLoading}
							className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
							aria-label="Refresh history">
							<svg
								className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
								/>
							</svg>
						</button>

						<button
							onClick={onClose}
							className="p-1.5 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
							aria-label="Close panel">
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</button>
					</div>
				</div>

				<div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
					<div className="relative flex-1 max-w-xs">
						<svg
							className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z"
							/>
						</svg>
						<input
							type="text"
							placeholder="Search by action…"
							value={actionFilterInput}
							onChange={(e) => handleSearchInput(e.target.value)}
							className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
						{actionFilterInput && (
							<button
								onClick={() => {
									setActionFilterInput("")
									setActionFilter("")
									setPage(1)
								}}
								className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
								<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</button>
						)}
					</div>

					<div className="flex items-center gap-2 ml-auto">
						<span className="text-xs text-gray-500 dark:text-gray-400">Rows:</span>
						<select
							value={pageSize}
							onChange={(e) => {
								setPageSize(Number(e.target.value))
								setPage(1)
							}}
							className="text-xs rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500">
							{PAGE_SIZE_OPTIONS.map((s) => (
								<option key={s} value={s}>
									{s}
								</option>
							))}
						</select>
					</div>
				</div>

				<div className="flex-1 overflow-auto">
					{isLoading ? (
						<div className="flex items-center justify-center h-40 text-gray-500 dark:text-gray-400">
							<svg
								className="w-6 h-6 animate-spin mr-2"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
								/>
							</svg>
							Loading history...
						</div>
					) : filtered.length === 0 ? (
						<div className="flex flex-col items-center justify-center h-40 text-gray-500 dark:text-gray-400 gap-2">
							<svg
								className="w-10 h-10 opacity-30"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
								/>
							</svg>
							<span className="text-sm">No history entries found</span>
						</div>
					) : (
						<table className="w-full text-sm">
							<thead className="sticky top-0 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
								<tr>
									<SortableTh field="action" label="Action" />
									<th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">
										Description
									</th>
									<SortableTh field="user_email" label="User" />
									<SortableTh field="createdAt" label="Created" />
									<SortableTh field="updated" label="Updated" />
									{!isReadOnly && <th className="px-4 py-3" />}
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-100 dark:divide-gray-800">
								{filtered.map((entry) => (
									<tr
										key={entry.id}
										className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
										<td className="px-4 py-3 font-mono text-xs text-blue-700 dark:text-blue-400 whitespace-nowrap">
											{entry.action}
										</td>
										<td className="px-4 py-3 text-gray-700 dark:text-gray-300 max-w-xs">
											{editingId === entry.id ? (
												<div className="flex flex-col gap-1.5">
													<textarea
														className="w-full text-sm border border-blue-400 dark:border-blue-500 rounded-md px-2 py-1 bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
														rows={3}
														value={editingValue}
														onChange={(e) => setEditingValue(e.target.value)}
														autoFocus
													/>
													<div className="flex gap-1.5">
														<button
															onClick={() => saveEdit(entry.id)}
															disabled={savingId === entry.id}
															className="px-2.5 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors">
															{savingId === entry.id ? "Saving..." : "Save"}
														</button>
														<button
															onClick={cancelEdit}
															className="px-2.5 py-1 text-xs rounded bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 transition-colors">
															Cancel
														</button>
													</div>
												</div>
											) : (
												<span className="break-words">{entry.description || "—"}</span>
											)}
										</td>
										<td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">
											{entry.user_email || entry.user_ID || "—"}
										</td>
										<td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">
											{formatDate(entry.createdAt)}
										</td>
										<td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">
											{formatDate(entry.updated)}
										</td>
										{!isReadOnly && (
											<td className="px-4 py-3">
												{editingId !== entry.id && (
													<button
														onClick={() => startEdit(entry)}
														className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition-colors whitespace-nowrap">
														<svg
															className="w-3 h-3"
															fill="none"
															stroke="currentColor"
															viewBox="0 0 24 24">
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																strokeWidth={2}
																d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
															/>
														</svg>
														Edit
													</button>
												)}
											</td>
										)}
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>

				{!isLoading && totalPages > 0 && (
					<div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
						<span className="text-xs text-gray-500 dark:text-gray-400">
							Page {page} of {totalPages} · {total} total
						</span>

						<div className="flex items-center gap-1">
							<button
								onClick={() => setPage(1)}
								disabled={page === 1}
								className="p-1.5 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
								aria-label="First page">
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M11 19l-7-7 7-7M18 19l-7-7 7-7"
									/>
								</svg>
							</button>
							<button
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={page === 1}
								className="p-1.5 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
								aria-label="Previous page">
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M15 19l-7-7 7-7"
									/>
								</svg>
							</button>

							{Array.from({length: Math.min(5, totalPages)}, (_, i) => {
								const start = Math.max(1, Math.min(page - 2, totalPages - 4))
								const p = start + i
								return (
									<button
										key={p}
										onClick={() => setPage(p)}
										className={`w-8 h-8 text-xs rounded transition-colors ${
											p === page
												? "bg-blue-600 text-white font-semibold"
												: "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
										}`}>
										{p}
									</button>
								)
							})}

							<button
								onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
								disabled={page === totalPages}
								className="p-1.5 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
								aria-label="Next page">
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M9 5l7 7-7 7"
									/>
								</svg>
							</button>
							<button
								onClick={() => setPage(totalPages)}
								disabled={page === totalPages}
								className="p-1.5 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
								aria-label="Last page">
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M13 5l7 7-7 7M6 5l7 7-7 7"
									/>
								</svg>
							</button>
						</div>
					</div>
				)}
			</div>
		</>
	)
}

export default CommandHistoryPanel
